import React, { useState, useRef, useEffect } from 'react';
import {
  Scan, Search, Package, CheckCircle, AlertCircle, X, Camera,
  Clock, MapPin, User, Hash, Weight, Calendar, Plus, Trash2,
  Save, FileText, Loader2, Volume2, VolumeX, Check
} from 'lucide-react';

// Mock scanned packages in current session
const mockScannedPackages = [
  {
    id: 'scan-001',
    tracking_number: 'TRK-2025-45821',
    bolt_number: 'BOLT-FS-4521',
    weight_lb: 12.5,
    customer_name: 'Marcus Johnson',
    matched: true,
    scanned_at: '2025-12-15T10:30:15',
  },
  {
    id: 'scan-002',
    tracking_number: 'TRK-2025-45820',
    bolt_number: 'BOLT-FS-4520',
    weight_lb: 8.2,
    customer_name: 'Sarah Williams',
    matched: true,
    scanned_at: '2025-12-15T10:32:45',
  },
];

// Mock recent intake sessions
const recentSessions = [
  { id: 'sess-001', date: 'Dec 15, 2025', packages: 47, status: 'in_progress' },
  { id: 'sess-002', date: 'Dec 14, 2025', packages: 82, status: 'completed' },
  { id: 'sess-003', date: 'Dec 13, 2025', packages: 63, status: 'completed' },
];

export default function TenantIntake() {
  const [activeSession, setActiveSession] = useState({
    id: 'sess-001',
    date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    manifest_code: 'MAN-2025-12-15',
    packages: mockScannedPackages,
    status: 'in_progress'
  });
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState(mockScannedPackages);
  const inputRef = useRef(null);

  // Focus input when scanning mode is active
  useEffect(() => {
    if (isScanning && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isScanning]);

  // Simulate barcode scan
  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;

    // Simulate API lookup
    const mockPackageData = {
      id: `scan-${Date.now()}`,
      tracking_number: barcodeInput.toUpperCase(),
      bolt_number: `BOLT-FS-${Math.floor(Math.random() * 9000) + 1000}`,
      weight_lb: (Math.random() * 20 + 2).toFixed(1),
      customer_name: ['John Smith', 'Mary Johnson', 'David Brown', 'Lisa Davis'][Math.floor(Math.random() * 4)],
      matched: Math.random() > 0.2,
      scanned_at: new Date().toISOString(),
    };

    // Show scan result feedback
    setScanResult({
      success: true,
      package: mockPackageData,
    });

    // Add to history
    setScanHistory(prev => [mockPackageData, ...prev]);

    // Play sound (simulated)
    if (soundEnabled) {
      console.log('🔊 Beep!');
    }

    // Clear input and result after delay
    setBarcodeInput('');
    setTimeout(() => setScanResult(null), 3000);
  };

  const handleRemovePackage = (id) => {
    setScanHistory(prev => prev.filter(p => p.id !== id));
  };

  const stats = {
    total: scanHistory.length,
    matched: scanHistory.filter(p => p.matched).length,
    unmatched: scanHistory.filter(p => !p.matched).length,
    totalWeight: scanHistory.reduce((sum, p) => sum + parseFloat(p.weight_lb), 0).toFixed(1),
  };

  return (
    <div className="tenant-intake-page" data-testid="tenant-intake">
      {/* Session Header */}
      <div className="intake-session-header">
        <div className="session-info">
          <div className="session-badge active">
            <Clock size={14} />
            Active Session
          </div>
          <h2>Intake Session - {activeSession.date}</h2>
          <span className="manifest-code">Manifest: {activeSession.manifest_code}</span>
        </div>
        <div className="session-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowStartModal(true)}
            data-testid="new-session-btn"
          >
            <Plus size={14} /> New Session
          </button>
          <button 
            className="btn btn-primary"
            data-testid="finalize-session-btn"
          >
            <CheckCircle size={14} /> Finalize Session
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="intake-stats-row">
        <div className="stat-card">
          <Package size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Packages Scanned</span>
          </div>
        </div>
        <div className="stat-card matched">
          <CheckCircle size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.matched}</span>
            <span className="stat-label">Matched</span>
          </div>
        </div>
        <div className="stat-card unmatched">
          <AlertCircle size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.unmatched}</span>
            <span className="stat-label">Unmatched</span>
          </div>
        </div>
        <div className="stat-card">
          <Weight size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.totalWeight} lbs</span>
            <span className="stat-label">Total Weight</span>
          </div>
        </div>
      </div>

      {/* Scanner Section */}
      <div className="scanner-section">
        <div className="scanner-card">
          <div className="scanner-header">
            <div className="scanner-title">
              <Scan size={24} />
              <div>
                <h3>Barcode Scanner</h3>
                <span className="scanner-subtitle">Scan or enter tracking number</span>
              </div>
            </div>
            <div className="scanner-controls">
              <button 
                className={`sound-toggle ${soundEnabled ? 'active' : ''}`}
                onClick={() => setSoundEnabled(!soundEnabled)}
                data-testid="sound-toggle"
              >
                {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <button 
                className={`scan-mode-btn ${isScanning ? 'active' : ''}`}
                onClick={() => setIsScanning(!isScanning)}
                data-testid="scan-mode-btn"
              >
                <Camera size={18} />
                {isScanning ? 'Stop Scanning' : 'Start Scanning'}
              </button>
            </div>
          </div>

          <form onSubmit={handleBarcodeSubmit} className="scanner-input-form">
            <div className={`scanner-input-wrapper ${isScanning ? 'active' : ''}`}>
              <Search size={20} />
              <input
                ref={inputRef}
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder={isScanning ? 'Waiting for scan...' : 'Enter tracking number manually'}
                className="scanner-input"
                data-testid="barcode-input"
              />
              {barcodeInput && (
                <button 
                  type="button" 
                  className="clear-btn"
                  onClick={() => setBarcodeInput('')}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button 
              type="submit" 
              className="btn btn-primary scan-btn"
              disabled={!barcodeInput.trim()}
              data-testid="scan-submit-btn"
            >
              <Scan size={16} /> Scan
            </button>
          </form>

          {/* Scan Result Feedback */}
          {scanResult && (
            <div className={`scan-result ${scanResult.success ? 'success' : 'error'}`} data-testid="scan-result">
              <div className="result-icon">
                {scanResult.success ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
              </div>
              <div className="result-content">
                <span className="result-title">
                  {scanResult.success ? 'Package Scanned Successfully' : 'Scan Error'}
                </span>
                {scanResult.package && (
                  <div className="result-details">
                    <span>{scanResult.package.tracking_number}</span>
                    <span className="separator">•</span>
                    <span>{scanResult.package.customer_name}</span>
                    <span className="separator">•</span>
                    <span>{scanResult.package.weight_lb} lbs</span>
                  </div>
                )}
              </div>
              {scanResult.package && !scanResult.package.matched && (
                <div className="result-warning">
                  <AlertCircle size={14} />
                  Customer not matched
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Sessions */}
        <div className="recent-sessions-card">
          <h3>Recent Sessions</h3>
          <div className="sessions-list">
            {recentSessions.map((session) => (
              <div 
                key={session.id} 
                className={`session-item ${session.status}`}
                data-testid={`session-${session.id}`}
              >
                <div className="session-item-info">
                  <Calendar size={14} />
                  <span className="session-date">{session.date}</span>
                </div>
                <div className="session-item-stats">
                  <span className="package-count">{session.packages} pkgs</span>
                  <span className={`status-dot ${session.status}`}></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scanned Packages Table */}
      <div className="scanned-packages-section">
        <div className="section-header">
          <h3>Scanned Packages ({scanHistory.length})</h3>
          <div className="section-actions">
            <button className="btn btn-ghost btn-sm" data-testid="export-scans">
              <FileText size={14} /> Export
            </button>
          </div>
        </div>

        <div className="scanned-packages-table">
          <div className="table-header">
            <span>Tracking #</span>
            <span>BOLT #</span>
            <span>Customer</span>
            <span>Weight</span>
            <span>Status</span>
            <span>Scanned At</span>
            <span>Actions</span>
          </div>
          
          {scanHistory.map((pkg) => (
            <div key={pkg.id} className="table-row" data-testid={`scanned-${pkg.id}`}>
              <span className="tracking mono">{pkg.tracking_number}</span>
              <span className="bolt-number mono">{pkg.bolt_number}</span>
              <span className="customer">
                <User size={12} />
                {pkg.customer_name}
              </span>
              <span className="weight">{pkg.weight_lb} lbs</span>
              <span className={`match-status ${pkg.matched ? 'matched' : 'unmatched'}`}>
                {pkg.matched ? (
                  <><CheckCircle size={12} /> Matched</>
                ) : (
                  <><AlertCircle size={12} /> Unmatched</>
                )}
              </span>
              <span className="timestamp">
                {new Date(pkg.scanned_at).toLocaleTimeString()}
              </span>
              <div className="actions">
                <button 
                  className="action-btn view"
                  onClick={() => {
                    setSelectedPackage(pkg);
                    setShowPackageModal(true);
                  }}
                  data-testid={`view-${pkg.id}`}
                >
                  <Search size={14} />
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleRemovePackage(pkg.id)}
                  data-testid={`remove-${pkg.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}

          {scanHistory.length === 0 && (
            <div className="empty-state">
              <Scan size={48} />
              <h4>No packages scanned</h4>
              <p>Start scanning packages to add them to this session</p>
            </div>
          )}
        </div>
      </div>

      {/* New Session Modal */}
      {showStartModal && (
        <div className="modal-overlay" onClick={() => setShowStartModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Start New Intake Session</h3>
              <button className="close-btn" onClick={() => setShowStartModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Manifest Code</label>
                <input 
                  type="text" 
                  placeholder="e.g., MAN-2025-12-16"
                  data-testid="new-manifest-code"
                />
              </div>
              <div className="form-group">
                <label>Session Notes (Optional)</label>
                <textarea 
                  placeholder="Add any notes about this intake session..."
                  rows={3}
                  data-testid="session-notes"
                />
              </div>
              <div className="warning-box">
                <AlertCircle size={16} />
                <span>Starting a new session will close the current active session.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowStartModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="start-session-confirm">
                <Plus size={14} /> Start Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Package Detail Modal */}
      {showPackageModal && selectedPackage && (
        <div className="modal-overlay" onClick={() => setShowPackageModal(false)}>
          <div className="modal-content package-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Package Details</h3>
              <button className="close-btn" onClick={() => setShowPackageModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">Tracking Number</span>
                  <span className="value mono">{selectedPackage.tracking_number}</span>
                </div>
                <div className="detail-item">
                  <span className="label">BOLT Number</span>
                  <span className="value mono">{selectedPackage.bolt_number}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Customer</span>
                  <span className="value">{selectedPackage.customer_name}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Weight</span>
                  <span className="value">{selectedPackage.weight_lb} lbs</span>
                </div>
                <div className="detail-item">
                  <span className="label">Match Status</span>
                  <span className={`value ${selectedPackage.matched ? 'matched' : 'unmatched'}`}>
                    {selectedPackage.matched ? 'Matched' : 'Unmatched'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Scanned At</span>
                  <span className="value">{new Date(selectedPackage.scanned_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPackageModal(false)}>
                Close
              </button>
              {!selectedPackage.matched && (
                <button className="btn btn-primary" data-testid="match-customer-btn">
                  <User size={14} /> Match to Customer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
